import type { IntakeSummaryType } from '@/contracts/aeris/uniforms.ts'
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'
import { clsx } from 'clsx'
import {
  Check,
  ClipboardCheck,
  Copy,
  ExternalLink,
  Link2,
  MessageCircle,
  Pencil,
  Plus,
  Search,
  X,
} from 'lucide-react'
import { useDeferredValue, useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router'

import { fetchPieces, fetchFabrics, storeKeys } from '@/api/uniform-store'
import { fetchCustomers } from '@/api/store'
import { cancelIntake, convertIntake, createIntake, reopenIntake } from '@/api/uniforms'
import { useToast } from '@/components/Toast'
import {
  Badge,
  Button,
  Empty,
  ErrorBox,
  Modal,
  PageHeader,
  Spinner,
} from '@/components/ui'
import { day } from '@/lib/format'
import { errorMessage } from '@/lib/http'
import { useAccess } from '@/lib/access'
import { intakeChanges } from '@/lib/intake-changes'
import { tallyLabel } from '@/lib/intake-offer'
import { keys, useIntake, useIntakes } from '@/lib/queries'
import { orderRef, fabricLabel } from '@/lib/uniforms'

type Tone = 'neutral' | 'info' | 'warning' | 'danger' | 'success' | 'lime'
type Fabric = IntakeSummaryType['fabricIds'][number]

const sameSet = (left: readonly string[], right: readonly string[]): boolean =>
  left.length === right.length && left.every((value) => right.includes(value))

const STATUS_META: Record<
  IntakeSummaryType['status'],
  { label: string; tone: Tone }
> = {
  aberto: { label: 'Esperando o cliente', tone: 'info' },
  enviado: { label: 'Enviado pelo cliente', tone: 'warning' },
  convertido: { label: 'Virou pedido', tone: 'success' },
  cancelado: { label: 'Cancelado', tone: 'neutral' },
  expirado: { label: 'Venceu', tone: 'neutral' },
}

const TABS = [
  { key: 'enviado', label: 'Para conferir' },
  { key: 'aberto', label: 'Esperando o cliente' },
  { key: 'expirado', label: 'Vencidos' },
  { key: undefined, label: 'Todos' },
] as const

export const linkUrl = (token: string): string =>
  `${window.location.origin}/pedido/${token}`

const whatsappUrl = (intake: IntakeSummaryType): string | null => {
  const digits = intake.contactPhone?.replace(/\D/g, '') ?? ''
  if (digits.length < 10) return null
  const phone = digits.startsWith('55') ? digits : `55${digits}`
  const text = intake.reopenedAt
    ? `Oi! Reabrimos o seu pedido da 4Play: mude o que precisar e envie de novo por aqui. ${linkUrl(intake.token)}`
    : `Oi! Monte o seu pedido da 4Play por aqui: escolha as peças e preencha nome, número e tamanho de cada um. ${linkUrl(intake.token)}`
  return `https://wa.me/${phone}?text=${encodeURIComponent(text)}`
}

/**
 * Os links de pedido para o cliente (F232 do Aeris): gerar, mandar pelo
 * WhatsApp, acompanhar e conferir o que o cliente preencheu.
 */
export const LinksPage = () => {
  const access = useAccess()
  const [tab, setTab] = useState<(typeof TABS)[number]['key']>('enviado')
  // O vencido é um aberto que passou da validade: só a tela o separa.
  const list = useIntakes(tab === 'expirado' ? 'aberto' : tab)
  const shown = list.data?.filter((intake) =>
    tab === 'aberto' || tab === 'expirado' ? intake.status === tab : true
  )
  const [creating, setCreating] = useState(false)
  const [created, setCreated] = useState<IntakeSummaryType | null>(null)
  const [reviewing, setReviewing] = useState<string | null>(null)

  return (
    <div>
      <PageHeader
        kicker="O cliente monta a grade"
        title="Links do cliente"
        actions={
          <Button
            variant="lime"
            icon={<Plus className="h-4 w-4" />}
            onClick={() => setCreating(true)}
          >
            Gerar link
          </Button>
        }
      >
        <p className="mt-2 text-sm font-semibold text-muted">
          Mande o link; o cliente escolhe as peças e preenche nome, número e
          tamanho (e o cadastro, se for novo). Você confere e o pedido nasce.
        </p>
      </PageHeader>

      <div className="mb-4 flex flex-wrap gap-2">
        {TABS.map((option) => (
          <button
            key={option.label}
            type="button"
            onClick={() => setTab(option.key)}
            className={clsx(
              'rounded-full px-3 py-1.5 text-xs font-bold',
              tab === option.key ? 'bg-ink text-white' : 'bg-black/5'
            )}
          >
            {option.label}
          </button>
        ))}
      </div>

      {list.isPending && <Spinner />}
      {list.isError && (
        <ErrorBox
          message={errorMessage(list.error)}
          onRetry={() => void list.refetch()}
        />
      )}
      {shown?.length === 0 && (
        <Empty
          title={tab === 'enviado' ? 'Nada para conferir' : tab === 'expirado' ? 'Nenhum link vencido' : 'Nenhum link aqui'}
        >
          <p>Gere um link e mande para o cliente pelo WhatsApp.</p>
        </Empty>
      )}

      <div className="space-y-2">
        {shown?.map((intake) => (
          <IntakeRow
            key={intake.id}
            intake={intake}
            canReview={access.createOrder}
            onReview={() => setReviewing(intake.id)}
            onReopened={setCreated}
          />
        ))}
      </div>

      <CreateDialog
        open={creating}
        onClose={() => setCreating(false)}
        onCreated={(intake) => {
          setCreating(false)
          setCreated(intake)
        }}
      />
      <CreatedDialog intake={created} onClose={() => setCreated(null)} />
      <ReviewDialog id={reviewing} onClose={() => setReviewing(null)} />
    </div>
  )
}

const CopyButton = ({ intake }: { intake: IntakeSummaryType }) => {
  const [copied, setCopied] = useState(false)
  return (
    <Button
      size="sm"
      variant="outline"
      icon={
        copied ? (
          <Check className="h-3.5 w-3.5" />
        ) : (
          <Copy className="h-3.5 w-3.5" />
        )
      }
      onClick={() => {
        void navigator.clipboard.writeText(linkUrl(intake.token)).then(() => {
          setCopied(true)
          setTimeout(() => setCopied(false), 2000)
        })
      }}
    >
      {copied ? 'Copiado' : 'Copiar link'}
    </Button>
  )
}

const IntakeRow = ({
  intake,
  canReview,
  onReview,
  onReopened,
}: {
  intake: IntakeSummaryType
  canReview: boolean
  onReview: () => void
  onReopened: (intake: IntakeSummaryType) => void
}) => {
  const queryClient = useQueryClient()
  const meta = STATUS_META[intake.status]
  const wa = whatsappUrl(intake)
  const cancel = useMutation({
    mutationFn: () => cancelIntake(intake.id, intake.version),
    onSuccess: () =>
      void queryClient.invalidateQueries({ queryKey: keys.intakesAll }),
  })
  const reopen = useMutation({
    mutationFn: () => reopenIntake(intake.id, intake.version),
    onSuccess: (reopened) => {
      void queryClient.invalidateQueries({ queryKey: keys.intakesAll })
      onReopened(reopened)
    },
  })
  const who =
    intake.submittedName ??
    intake.customer?.name ??
    intake.contactName ??
    intake.contactPhone ??
    intake.contactEmail ??
    'Cliente'

  return (
    <article className="card p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="truncate font-extrabold">{who}</p>
          <p className="text-xs text-muted">
            {intake.customer ? 'Cliente cadastrado' : 'Cliente novo'}
            {intake.contactPhone && ` · ${intake.contactPhone}`} · gerado{' '}
            {day(intake.createdAt)}
            {intake.createdByName && ` por ${intake.createdByName}`}
            {intake.status === 'aberto' &&
              ` · vale até ${day(intake.expiresAt)}`}
            {intake.status === 'aberto' &&
              intake.reopenedAt &&
              ` · reaberto ${day(intake.reopenedAt)} para ele editar`}
            {intake.submittedAt &&
              ` · ${intake.reopenedAt ? 'editado' : 'enviado'} ${day(intake.submittedAt)} · ${tallyLabel(intake.submittedSets, intake.submittedPieces)}`}
          </p>
        </div>
        <Badge tone={meta.tone}>{meta.label}</Badge>
      </div>
      <div className="mt-3 flex flex-wrap gap-2">
        {canReview && intake.status === 'enviado' && (
          <Button
            size="sm"
            variant="ink"
            icon={<ClipboardCheck className="h-3.5 w-3.5" />}
            onClick={onReview}
          >
            Conferir
          </Button>
        )}
        {canReview && intake.status === 'enviado' && (
          <Button
            size="sm"
            variant="outline"
            icon={<Pencil className="h-3.5 w-3.5" />}
            busy={reopen.isPending}
            onClick={() => {
              if (
                window.confirm(
                  'Reabrir o link para o cliente editar? Ele volta para "Esperando o cliente" até o cliente enviar de novo.'
                )
              )
                reopen.mutate()
            }}
          >
            Reabrir para o cliente
          </Button>
        )}
        {intake.status === 'aberto' && <CopyButton intake={intake} />}
        {intake.status === 'aberto' && wa && (
          <a href={wa} target="_blank" rel="noreferrer">
            <Button
              size="sm"
              variant="outline"
              icon={<MessageCircle className="h-3.5 w-3.5" />}
            >
              WhatsApp
            </Button>
          </a>
        )}
        {intake.order && (
          <Link to={`/pedidos/${intake.order.id}`}>
            <Button
              size="sm"
              variant="outline"
              icon={<ExternalLink className="h-3.5 w-3.5" />}
            >
              Pedido {orderRef(intake.order)}
            </Button>
          </Link>
        )}
        {canReview && (intake.status === 'aberto' || intake.status === 'enviado') && (
          <Button
            size="sm"
            variant="ghost"
            icon={<X className="h-3.5 w-3.5" />}
            busy={cancel.isPending}
            onClick={() => {
              if (
                window.confirm(
                  'Cancelar este link? O cliente não consegue mais enviar.'
                )
              )
                cancel.mutate()
            }}
          >
            Cancelar
          </Button>
        )}
      </div>
      {(cancel.isError || reopen.isError) && (
        <p className="mt-2 text-xs font-semibold text-red-700">
          {errorMessage(cancel.error ?? reopen.error)}
        </p>
      )}
    </article>
  )
}

const CreateDialog = ({
  open,
  onClose,
  onCreated,
}: {
  open: boolean
  onClose: () => void
  onCreated: (intake: IntakeSummaryType) => void
}) => {
  const queryClient = useQueryClient()
  const [search, setSearch] = useState('')
  const deferred = useDeferredValue(search.trim())
  const [customer, setCustomer] = useState<{
    id: string
    name: string
    phone: string | null
  } | null>(null)
  const [name, setName] = useState('')
  const [phone, setPhone] = useState('')
  const [email, setEmail] = useState('')
  const [showPrices, setShowPrices] = useState(false)
  const [restrict, setRestrict] = useState(false)
  const [productIds, setProductIds] = useState<string[]>([])
  const [fabrics, setFabrics] = useState<Fabric[] | null>(null)
  // Os acabamentos desmarcados, por tecido: sem nada aqui, o link oferece todos.
  const [finishesOff, setFinishesOff] = useState<Record<string, string[]>>({})
  const [days, setDays] = useState(30)
  const [leadDays, setLeadDays] = useState(30)
  const [message, setMessage] = useState('')

  useEffect(() => {
    if (!open) return
    setSearch('')
    setCustomer(null)
    setName('')
    setPhone('')
    setEmail('')
    setShowPrices(false)
    setRestrict(false)
    setProductIds([])
    setFabrics(null)
    setFinishesOff({})
    setDays(30)
    setLeadDays(30)
    setMessage('')
  }, [open])

  const customers = useQuery({
    queryKey: ['customers', 'intake', deferred],
    queryFn: ({ signal }) =>
      fetchCustomers({ search: deferred, pageSize: 6, page: 1 }, signal),
    enabled: open && deferred.length >= 2 && customer === null,
    placeholderData: keepPreviousData,
  })
  // A tabela da loja (F258 do Aeris): as peças com o descontinuado, e os tecidos.
  const catalog = useQuery({
    queryKey: storeKeys.pieces,
    queryFn: ({ signal }) => fetchPieces(signal),
    enabled: open,
    staleTime: 60_000,
  })
  const fabricList = useQuery({
    queryKey: storeKeys.fabrics,
    queryFn: ({ signal }) => fetchFabrics(signal),
    enabled: open,
  })
  const pieces = catalog.data ?? []
  const defaultFabrics =
    fabricList.data?.filter((f) => !f.discontinued).map((f) => f.id) ?? []
  const chosenFabrics = fabrics ?? defaultFabrics
  const offeredFabrics =
    fabricList.data?.filter((f) =>
      pieces.some((p) =>
        p.fabrics.some((v) => v.fabricId === f.id && !v.fabricArchived)
      )
    ) ?? []

  const create = useMutation({
    mutationFn: () =>
      createIntake({
        customerPartyId: customer?.id ?? null,
        contactName: customer ? null : name.trim() || null,
        phone: customer ? (customer.phone ?? null) : phone.trim() || null,
        email: customer ? null : email.trim() || null,
        showPrices,
        productIds: restrict ? productIds : [],
        // Os tecidos de sempre (os não descontinuados) vão vazios: o link segue a tabela da loja.
        fabricIds:
          fabrics === null || sameSet(fabrics, defaultFabrics) ? [] : fabrics,
        fabricFinishes: chosenFabrics.flatMap((fabricId) => {
          const off = finishesOff[fabricId] ?? []
          const own = fabricList.data?.find((f) => f.id === fabricId)?.finishes ?? []
          return off.length === 0
            ? []
            : [{ fabricId, finishes: own.filter((finish) => !off.includes(finish)) }]
        }),
        expiresInDays: days,
        leadDays,
        message: message.trim() || null,
      }),
    onSuccess: (intake) => {
      void queryClient.invalidateQueries({ queryKey: keys.intakesAll })
      onCreated(intake)
    },
  })

  const ready =
    (customer !== null ||
      (name.trim().length >= 2 &&
        (phone.replace(/\D/g, '').length >= 10 || email.includes('@')))) &&
    (!restrict || productIds.length > 0) &&
    (offeredFabrics.length === 0 || chosenFabrics.length > 0)

  return (
    <Modal
      open={open}
      wide
      title="Gerar link para o cliente"
      onClose={() => {
        create.reset()
        onClose()
      }}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancelar
          </Button>
          <Button
            variant="lime"
            icon={<Link2 className="h-4 w-4" />}
            busy={create.isPending}
            disabled={!ready || !catalog.isSuccess || !fabricList.isSuccess}
            onClick={() => create.mutate()}
          >
            Gerar link
          </Button>
        </>
      }
    >
      <div className="space-y-5">
        <div>
          <p className="mb-2 text-xs font-extrabold tracking-wide uppercase">
            Cliente
          </p>
          {customer ? (
            <div className="flex items-center justify-between gap-2 rounded-2xl bg-lime/40 px-4 py-3">
              <span className="text-sm font-bold">
                {customer.name}
                {customer.phone ? ` · ${customer.phone}` : ''}
              </span>
              <button
                type="button"
                onClick={() => setCustomer(null)}
                className="text-xs font-bold underline"
              >
                Trocar
              </button>
            </div>
          ) : (
            <>
              <div className="relative">
                <Search className="pointer-events-none absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 text-muted" />
                <input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Já é cliente? Procure pelo nome ou documento"
                  className="field pl-9"
                />
              </div>
              {customers.data && customers.data.items.length > 0 && (
                <div className="mt-2 space-y-1">
                  {customers.data.items.map((party) => (
                    <button
                      key={party.id}
                      type="button"
                      onClick={() =>
                        setCustomer({
                          id: party.id,
                          name: party.tradeName ?? party.legalName,
                          phone: party.mainPhone ?? null,
                        })
                      }
                      className="block w-full rounded-xl border border-line px-3 py-2 text-left text-sm hover:border-ink/30"
                    >
                      <strong>{party.tradeName ?? party.legalName}</strong>
                      {party.mainPhone && (
                        <span className="text-muted"> · {party.mainPhone}</span>
                      )}
                    </button>
                  ))}
                </div>
              )}
              <p className="mt-3 mb-2 text-xs text-muted">
                Cliente novo? Diga o nome e o WhatsApp ou o e-mail — ele completa
                o cadastro no link. Se o número já for de um cliente, o link fica
                no nome dele.
              </p>
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                maxLength={200}
                placeholder="Nome do cliente ou do time *"
                aria-label="Nome do cliente"
                className="field mb-3"
              />
              <div className="grid gap-3 sm:grid-cols-2">
                <input
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  inputMode="tel"
                  placeholder="WhatsApp com DDD"
                  className="field"
                />
                <input
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  inputMode="email"
                  placeholder="E-mail (opcional)"
                  className="field"
                />
              </div>
            </>
          )}
        </div>

        <div className="grid gap-3 sm:grid-cols-2">
          <label className="flex items-start gap-2 rounded-2xl border border-line p-3 text-sm">
            <input
              type="checkbox"
              checked={showPrices}
              onChange={(e) => setShowPrices(e.target.checked)}
              className="mt-1"
            />
            <span>
              <strong>Mostrar o preço</strong>
              <span className="block text-xs text-muted">
                O cliente vê o preço de cada peça (da tabela).
              </span>
            </span>
          </label>
          <label className="flex items-start gap-2 rounded-2xl border border-line p-3 text-sm">
            <input
              type="checkbox"
              checked={restrict}
              onChange={(e) => {
                setRestrict(e.target.checked)
                // Começa com as peças da tabela; a descontinuada fica desmarcada (liga quem precisa).
                if (e.target.checked && productIds.length === 0)
                  setProductIds(
                    pieces
                      .filter((product) => !product.discontinued)
                      .map((product) => product.id)
                  )
              }}
              className="mt-1"
            />
            <span>
              <strong>Escolher as peças</strong>
              <span className="block text-xs text-muted">
                Sem marcar, ele escolhe entre as peças da tabela (menos as
                descontinuadas).
              </span>
            </span>
          </label>
        </div>

        {restrict && (
          <div>
            <p className="mb-2 text-xs font-extrabold tracking-wide uppercase">
              Peças que ele pode escolher
            </p>
            {catalog.isPending && <Spinner />}
            <div className="grid max-h-60 gap-1.5 overflow-y-auto sm:grid-cols-2">
              {pieces.map((product) => {
                const on = productIds.includes(product.id)
                return (
                  <button
                    key={product.id}
                    type="button"
                    onClick={() =>
                      setProductIds((ids) =>
                        on
                          ? ids.filter((id) => id !== product.id)
                          : [...ids, product.id]
                      )
                    }
                    className={clsx(
                      'rounded-xl border-2 px-3 py-2 text-left text-sm font-semibold',
                      on ? 'border-ink bg-lime' : 'border-line'
                    )}
                  >
                    {product.name}
                    {product.discontinued && (
                      <span className="ml-1 text-[11px] font-bold text-amber-800">
                        · descontinuada
                      </span>
                    )}
                  </button>
                )
              })}
            </div>
          </div>
        )}

        {offeredFabrics.length > 0 && (
          <div>
            <p className="mb-2 text-xs font-extrabold tracking-wide uppercase">
              Tecidos que ele pode escolher
            </p>
            <div className="space-y-2">
              {offeredFabrics.map((entry) => {
                const on = chosenFabrics.includes(entry.id)
                const off = finishesOff[entry.id] ?? []
                return (
                  <div key={entry.id} className="flex flex-wrap items-center gap-2">
                    <button
                      type="button"
                      onClick={() =>
                        setFabrics(
                          on
                            ? chosenFabrics.filter((f) => f !== entry.id)
                            : [...chosenFabrics, entry.id]
                        )
                      }
                      className={clsx(
                        'rounded-full border-2 px-3 py-1.5 text-xs font-extrabold',
                        on ? 'border-ink bg-lime' : 'border-line'
                      )}
                    >
                      {entry.name}
                      {entry.discontinued && (
                        <span className="ml-1 font-bold text-amber-800">
                          · descontinuado
                        </span>
                      )}
                    </button>
                    {on &&
                      entry.finishes.map((finish) => {
                        const kept = !off.includes(finish)
                        return (
                          <button
                            key={finish}
                            type="button"
                            aria-pressed={kept}
                            // O tecido marcado fica com ao menos um acabamento.
                            disabled={kept && entry.finishes.length - off.length === 1}
                            onClick={() =>
                              setFinishesOff((all) => ({
                                ...all,
                                [entry.id]: kept
                                  ? [...off, finish]
                                  : off.filter((value) => value !== finish),
                              }))
                            }
                            className={clsx(
                              'rounded-full border-2 px-2.5 py-1 text-[11px] font-bold',
                              kept ? 'border-ink bg-lime/50' : 'border-line text-muted'
                            )}
                          >
                            {finish}
                          </button>
                        )
                      })}
                  </div>
                )
              })}
            </div>
            <p className="mt-1 text-xs text-muted">
              O cliente escolhe entre os acabamentos marcados. O descontinuado só
              aparece para ele se você marcar aqui.
            </p>
          </div>
        )}

        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs font-extrabold tracking-wide uppercase">
            Vale por
          </span>
          {[3, 7, 15, 30].map((option) => (
            <button
              key={option}
              type="button"
              onClick={() => setDays(option)}
              className={clsx(
                'rounded-full border-2 px-3 py-1 text-xs font-extrabold',
                days === option ? 'border-ink bg-lime' : 'border-line'
              )}
            >
              {option} dias
            </button>
          ))}
        </div>

        <div>
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs font-extrabold tracking-wide uppercase">
              Prazo de produção
            </span>
            {[15, 20, 30, 45].map((option) => (
              <button
                key={option}
                type="button"
                onClick={() => setLeadDays(option)}
                className={clsx(
                  'rounded-full border-2 px-3 py-1 text-xs font-extrabold',
                  leadDays === option ? 'border-ink bg-lime' : 'border-line'
                )}
              >
                {option} dias
              </button>
            ))}
          </div>
          <p className="mt-1 text-xs text-muted">
            Sem evento, o despacho fica para o dia do envio mais este prazo. Com
            evento, uma semana antes da data dele.
          </p>
        </div>

        <label className="block">
          <span className="mb-1 block text-xs font-extrabold tracking-wide uppercase">
            Recado no topo da página (opcional)
          </span>
          <textarea
            rows={2}
            maxLength={500}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            className="field resize-y"
            placeholder="Oi! Preencha a grade do time até sexta, por favor."
          />
        </label>

        {(catalog.isError || fabricList.isError) && (
          <ErrorBox message={errorMessage(catalog.error ?? fabricList.error)} />
        )}
        {create.isError && <ErrorBox message={errorMessage(create.error)} />}
      </div>
    </Modal>
  )
}

const CreatedDialog = ({
  intake,
  onClose,
}: {
  intake: IntakeSummaryType | null
  onClose: () => void
}) => {
  const wa = intake ? whatsappUrl(intake) : null
  return (
    <Modal
      open={intake !== null}
      title={intake?.reopenedAt ? 'Link reaberto' : 'Link pronto'}
      onClose={onClose}
      footer={
        <Button variant="ink" onClick={onClose}>
          Fechar
        </Button>
      }
    >
      {intake && (
        <div className="space-y-4 text-sm">
          <p>
            {intake.submittedName
              ? `Link de ${intake.submittedName}`
              : 'Link de cliente novo'}{' '}
            — vale até <strong>{day(intake.expiresAt)}</strong>.
            {intake.reopenedAt &&
              ' Mande para o cliente: a página abre com o que ele já tinha preenchido.'}
          </p>
          <p className="rounded-xl bg-paper p-3 font-mono text-xs break-all">
            {linkUrl(intake.token)}
          </p>
          <div className="flex flex-wrap gap-2">
            <CopyButton intake={intake} />
            {wa && (
              <a href={wa} target="_blank" rel="noreferrer">
                <Button
                  size="sm"
                  variant="lime"
                  icon={<MessageCircle className="h-3.5 w-3.5" />}
                >
                  Mandar pelo WhatsApp
                </Button>
              </a>
            )}
          </div>
        </div>
      )}
    </Modal>
  )
}

const ReviewDialog = ({
  id,
  onClose,
}: {
  id: string | null
  onClose: () => void
}) => {
  const navigate = useNavigate()
  const toast = useToast()
  const queryClient = useQueryClient()
  const detail = useIntake(id)
  const convert = useMutation({
    mutationFn: () => convertIntake(id ?? '', detail.data?.intake.version ?? 0),
    onSuccess: (order) => {
      toast(`Pedido ${orderRef(order)} criado. Ele está em Atendimento.`)
      void queryClient.invalidateQueries({ queryKey: keys.intakesAll })
      void queryClient.invalidateQueries({ queryKey: keys.queueAll })
      onClose()
      void navigate(`/pedidos/${order.id}`)
    },
  })
  const submission = detail.data?.submission ?? null
  const customer = submission?.customer ?? null
  const previous = detail.data?.previous ?? null
  const changes = previous && submission ? intakeChanges(previous, submission) : null

  return (
    <Modal
      open={id !== null}
      wide
      title="Conferir o pedido do cliente"
      onClose={() => {
        convert.reset()
        onClose()
      }}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Fechar
          </Button>
          <Button
            variant="lime"
            icon={<Check className="h-4 w-4" />}
            busy={convert.isPending}
            disabled={detail.data?.intake.status !== 'enviado'}
            onClick={() => convert.mutate()}
          >
            Está certo — criar o pedido
          </Button>
        </>
      }
    >
      {detail.isPending && <Spinner />}
      {detail.isError && <ErrorBox message={errorMessage(detail.error)} />}
      {detail.data && submission && (
        <div className="space-y-5 text-sm">
          {changes && (
            <section className="rounded-2xl border-2 border-amber-300 bg-amber-50 p-4">
              <p className="mb-2 text-xs font-extrabold tracking-wide uppercase">
                O que o cliente alterou
              </p>
              {changes.length === 0 && (
                <p>Ele enviou de novo sem mudar nada.</p>
              )}
              <div className="space-y-2">
                {changes.map((change) => (
                  <div key={change.title}>
                    <p className="font-extrabold">{change.title}</p>
                    <ul className="text-xs">
                      {change.lines.map((line) => (
                        <li key={line}>{line}</li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </section>
          )}
          {customer ? (
            <section className="rounded-2xl border border-line p-4">
              <p className="mb-2 text-xs font-extrabold tracking-wide uppercase">
                Cadastro novo — confira
              </p>
              <p className="font-extrabold">{customer.name}</p>
              <p className="text-xs text-muted">
                {customer.kind === 'company' ? 'Empresa' : 'Pessoa'}
                {customer.document && ` · ${customer.document}`} ·{' '}
                {customer.phone}
                {customer.email && ` · ${customer.email}`}
              </p>
              {customer.address && (
                <p className="mt-1 text-xs">
                  {customer.address.street}
                  {customer.address.number && `, ${customer.address.number}`}
                  {customer.address.complement &&
                    ` — ${customer.address.complement}`}
                  {customer.address.district &&
                    ` · ${customer.address.district}`}{' '}
                  · {customer.address.city}/{customer.address.uf}
                  {customer.address.cep && ` · CEP ${customer.address.cep}`}
                </p>
              )}
            </section>
          ) : (
            <p className="rounded-2xl bg-paper p-3">
              Cliente: <strong>{detail.data.intake.customer?.name}</strong>
            </p>
          )}

          {submission.items.map((item) => {
            // Só o número também é personalizado (F258 do Aeris).
            const named = item.rows.filter(
              (row) => row.name !== null || row.number !== null
            )
            const plain = item.rows.filter(
              (row) => row.name === null && row.number === null
            )
            const kit = item.kit
            // O conjunto por partes (F300): conta os conjuntos pela primeira parte.
            const firstPart = kit?.people[0]?.pieces[0]?.part ?? kit?.counts[0]?.part
            const total = kit
              ? kit.people.reduce((sum, person) => sum + person.quantity, 0) +
                kit.counts.filter((count) => count.part === firstPart).reduce((sum, count) => sum + count.quantity, 0)
              : item.rows.reduce((sum, row) => sum + row.quantity, 0)
            // Só a peça de duas partes ou mais é conjunto: a camiseta sozinha é peça.
            const isSet =
              kit !== null &&
              new Set([
                ...kit.people.flatMap((person) => person.pieces.map((piece) => piece.part)),
                ...kit.counts.map((count) => count.part),
              ]).size > 1
            return (
              <section
                key={item.productId}
                className="rounded-2xl border border-line p-4"
              >
                <p className="mb-2 font-extrabold">
                  {item.productName}
                  {item.color && <span className="font-semibold"> · cor: {item.color}</span>}{' '}
                  <span className="font-semibold text-muted">
                    · {total} {isSet ? 'conjunto(s)' : 'peças'}
                  </span>
                </p>
                {item.options.length > 0 && (
                  <p className="mb-2 text-xs font-bold">
                    {item.options.map((option) => option.name).join(' · ')}
                  </p>
                )}
                {named.length > 0 && (
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="text-left text-muted">
                        <th className="py-1">Nome</th>
                        <th>Nº</th>
                        <th>Tam.</th>
                        <th className="text-right">Qtde</th>
                      </tr>
                    </thead>
                    <tbody>
                      {named.map((row, index) => (
                        <tr key={index} className="border-t border-line">
                          <td className="py-1">{row.name ?? '—'}</td>
                          <td>{row.number ?? '—'}</td>
                          <td>{row.size}</td>
                          <td className="text-right">{row.quantity}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
                {kit && (
                  <div className="space-y-2 text-xs">
                    {kit.people.length > 0 && (
                      <table className="w-full">
                        <thead>
                          <tr className="text-left text-muted">
                            <th className="py-1">Personalizados — cada peça com o seu nome e número</th>
                            <th className="text-right">Qtde</th>
                          </tr>
                        </thead>
                        <tbody>
                          {kit.people.map((person, index) => (
                            <tr key={index} className="border-t border-line align-top">
                              <td className="py-1">
                                {person.pieces.map((piece) => (
                                  <span key={piece.part} className="block">
                                    {piece.piece} {piece.size}
                                    {(piece.name || piece.number) && (
                                      <strong> · {[piece.name, piece.number].filter(Boolean).join(' ')}</strong>
                                    )}
                                  </span>
                                ))}
                              </td>
                              <td className="text-right">{person.quantity}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                    {kit.counts.length > 0 && (
                      <p>
                        Sem nome:{' '}
                        {kit.counts.map((count) => `${String(count.quantity)} ${count.piece} ${count.size}`).join(' · ')}
                      </p>
                    )}
                  </div>
                )}
                {plain.length > 0 && (
                  <p className="mt-2 text-xs">
                    Sem nome:{' '}
                    {plain
                      .map((row) => `${String(row.quantity)} ${row.size}`)
                      .join(' · ')}
                  </p>
                )}
              </section>
            )
          })}
          <section
            className={clsx(
              'rounded-2xl p-4',
              submission.tight ? 'bg-amber-100 text-amber-900' : 'bg-paper'
            )}
          >
            <p className="mb-1 text-xs font-extrabold tracking-wide uppercase">
              Para quando
            </p>
            <p>
              {submission.event ? (
                <>
                  Evento
                  {submission.event.name
                    ? ` "${submission.event.name}"`
                    : ''}{' '}
                  em <strong>{day(submission.event.date)}</strong>.{' '}
                </>
              ) : (
                <>Sem evento ({detail.data.intake.leadDays} dias de prazo). </>
              )}
              Despacho calculado:{' '}
              <strong>{day(submission.dispatchDate)}</strong>
              {submission.tight && ' — prazo curto, confira se dá tempo.'}
            </p>
            <p className="mt-1">
              Tecido:{' '}
              <strong>
                {fabricLabel(submission) ?? 'não sabe — a loja ajuda'}
              </strong>
            </p>
          </section>
          {submission.notes && (
            <p className="rounded-2xl bg-paper p-3">
              <strong>Observação:</strong> {submission.notes}
            </p>
          )}
          <p className="text-xs text-muted">
            Ao criar, {customer ? 'o cadastro do cliente nasce, ' : ''}o pedido
            é confirmado pelo preço da tabela e entra em Atendimento com a grade
            e os nomes. Dá para ajustar tudo depois na ficha.
          </p>
          {convert.isError && (
            <ErrorBox message={errorMessage(convert.error)} />
          )}
        </div>
      )}
    </Modal>
  )
}
